import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  FiMic, 
  FiDownload,
  FiGlobe,
  FiActivity,
  FiZap,
  FiDroplet,
  FiBarChart,
  FiClock,
  FiCommand,
  FiSettings,
  FiMonitor,
  FiCheck,
  FiVolume2,
  FiClipboard,
  FiMicOff,
  FiCopy,
  FiX
} from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import api from '../utils/api';
import VocalsPerMinuteOverlay from '../components/VocalsPerMinuteOverlay';
import { useVoiceStream } from '../hooks/useVoiceStream';

interface VocalsStats {
  totalWords: number;
  totalCharacters: number;
  sessionsCount: number;
  timeSaved: number; // in minutes
  averageWPM: number;
  languagesUsed: string[];
  mostUsedPhrases: { phrase: string; count: number }[];
}

interface TranscriptionSession {
  text: string;
  language: string;
  timestamp: Date;
  wordCount: number;
  duration: number; // seconds
  wpm: number;
}

interface HotkeyConfig {
  primaryKey: 'CapsLock' | 'Shift' | 'Ctrl' | 'Alt';
  secondaryKey?: string;
  autoFormat: boolean;
  autoPaste: boolean;
  autoTranslate: boolean;
  targetLanguage?: string;
}

const VocalsPerMinute: React.FC = () => {
  const [isDesktopInstalled, setIsDesktopInstalled] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [desktopStatus, setDesktopStatus] = useState<'disconnected' | 'connected' | 'recording'>('disconnected');
  
  // Statistics
  const [stats, setStats] = useState<VocalsStats>({
    totalWords: 0,
    totalCharacters: 0,
    sessionsCount: 0,
    timeSaved: 0,
    averageWPM: 0,
    languagesUsed: ['English'],
    mostUsedPhrases: []
  });
  
  const [recentSessions, setRecentSessions] = useState<TranscriptionSession[]>([]);
  
  // Settings
  const [hotkeyConfig, setHotkeyConfig] = useState<HotkeyConfig>({
    primaryKey: 'Shift',
    autoFormat: true,
    autoPaste: true,
    autoTranslate: false
  });
  
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [translationLanguage, setTranslationLanguage] = useState('es');
  const [overlayEnabled, setOverlayEnabled] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [bubblePosition, setBubblePosition] = useState<'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'bottom-center'>('bottom-center');
  const [lastVpm, setLastVpm] = useState<number | null>(null);
  const [lastText, setLastText] = useState<string>('');
  
  // Test feature states
  const [isTestRecording, setIsTestRecording] = useState(false);
  const [testTranscription, setTestTranscription] = useState('');
  const [showTestResult, setShowTestResult] = useState(false);
  const [testRecordingTime, setTestRecordingTime] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const testTimerRef = useRef<NodeJS.Timeout | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  
  // Audio refs for water drop sounds
  const startSoundRef = useRef<HTMLAudioElement | null>(null);
  const endSoundRef = useRef<HTMLAudioElement | null>(null);

  // Initialize water drop sounds
  useEffect(() => {
    // Create water drop sound using Web Audio API
    const createWaterDropSound = (frequency: number): HTMLAudioElement => {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      const audio = new Audio();
      // Create a simple water drop sound effect
      audio.src = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBjiS2Oy9diMFl2z/7pqvb/7hnS0hH5FNz+v0kD0oeYvLM9vBAGbF6A2n5kJ/yCQF5u7yKqnm1pB4FSYTAQS7ksYEAAAAAA==';
      
      return audio;
    };
    
    startSoundRef.current = createWaterDropSound(800);
    endSoundRef.current = createWaterDropSound(600);
  }, []);

  // Web-only mode: no desktop VPM status
  useEffect(() => {
    setIsDesktopInstalled(false);
    setDesktopStatus('disconnected');
  }, []);

  // Play water drop sound
  const playWaterDropSound = (type: 'start' | 'end') => {
    if (!soundEnabled) return;
    
    const audio = type === 'start' ? startSoundRef.current : endSoundRef.current;
    if (audio) {
      audio.volume = 0.3;
      audio.play().catch(console.error);
    }
  };

  // Download desktop app
  const downloadDesktopApp = async () => {
    setIsDownloading(true);
    try {
      // Determine OS
      const platform = navigator.platform.toLowerCase();
      let downloadUrl = '/api/vocals-per-minute/download/';
      
      if (platform.includes('win')) {
        downloadUrl += 'windows';
      } else if (platform.includes('mac')) {
        downloadUrl += 'mac';
      } else if (platform.includes('linux')) {
        downloadUrl += 'linux';
      } else {
        throw new Error('Unsupported operating system');
      }

      // Generate installation config
      const configResponse = await api.post('/vocals-per-minute/generate-config', {
        hotkeyConfig,
        selectedLanguage,
        overlayEnabled,
        soundEnabled,
        bubblePosition
      });

      if (!configResponse.data.success) {
        throw new Error('Failed to generate configuration');
      }

      const configToken = configResponse.data.token;

      // Create download link
      const link = document.createElement('a');
      link.href = `${downloadUrl}?token=${configToken}`;
      link.download = platform.includes('win') ? 'VocalsPerMinute.exe' : 
                      platform.includes('mac') ? 'VocalsPerMinute.dmg' : 
                      'VocalsPerMinute.AppImage';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Vocals Per Minute downloading... Follow the installation wizard.', {
        duration: 5000,
        icon: '💧'
      });

      // Show installation instructions
      setTimeout(() => {
        showInstallationInstructions();
      }, 2000);

    } catch (error: any) {
      console.error('Download error:', error);
      toast.error(error.message || 'Failed to download desktop app');
    } finally {
      setIsDownloading(false);
    }
  };

  const showInstallationInstructions = () => {
    const instructions = `
💧 VOCALS PER MINUTE - INSTALLATION

1. Run the downloaded installer
2. The app will run in your system tray
3. Hold Shift to start recording
4. Release to stop and auto-paste text
5. Water drop sounds confirm start/stop

✨ Features:
• Voice input anywhere on your PC
• Auto-formats and punctuates text
• Saves to Windows WordPad automatically
• Real-time translation available
• Tracks your productivity stats

🎯 Quick Tips:
• Right-click tray icon for settings
• Ctrl+V to paste if auto-paste fails
• Check stats dashboard for insights
    `;
    
    alert(instructions);
  };

  // Update hotkey configuration
  const updateHotkeyConfig = async (config: Partial<HotkeyConfig>) => {
    const newConfig = { ...hotkeyConfig, ...config };
    setHotkeyConfig(newConfig);
    
    // Send to desktop app if connected
    // no-op in web-only mode
  };

  // Calculate time saved (based on average typing speed of 40 WPM vs speaking 150 WPM)
  const calculateTimeSaved = (words: number): number => {
    const typingTime = words / 40; // minutes
    const speakingTime = words / 150; // minutes
    return Math.max(0, typingTime - speakingTime);
  };

  // Format statistics display
  const formatTimeSaved = (minutes: number): string => {
    if (minutes < 60) {
      return `${Math.round(minutes)} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  // Test recording functions
  const startTestRecording = async () => {
    try {
      // State is already set in onMouseDown/onTouchStart
      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      streamRef.current = stream;
      audioChunksRef.current = [];
      
      // Setup MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await processTestRecording(audioBlob);
      };
      
      // Start recording
      mediaRecorder.start();
      setTestRecordingTime(0);
      setShowTestResult(false);
      
      // Play start sound
      playWaterDropSound('start');
      
      // Start timer
      testTimerRef.current = setInterval(() => {
        setTestRecordingTime(prev => prev + 1);
      }, 1000);
      
      toast('🎤 Recording... Release to stop', {
        duration: 2000,
        icon: '💧'
      });
    } catch (error) {
      console.error('Error starting test recording:', error);
      toast.error('Failed to access microphone. Please check permissions.');
    }
  };

  const stopTestRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      // State is already set in onMouseUp/onTouchEnd
      
      // Play end sound
      playWaterDropSound('end');
      
      // Stop timer
      if (testTimerRef.current) {
        clearInterval(testTimerRef.current);
        testTimerRef.current = null;
      }
      
      // Stop audio stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    }
  };

  const processTestRecording = async (audioBlob: Blob) => {
    try {
      // Validate recording length and size
      if (testRecordingTime < 1) {
        toast.error('Recording too short. Please speak for at least 1 second.');
        return;
      }

      if (audioBlob.size < 1000) {
        toast.error('Recording file too small. Please try again.');
        return;
      }

      toast('Processing your voice...', {
        duration: 2000,
        icon: '⚡'
      });

      // Create FormData
      const formData = new FormData();
      formData.append('audio', audioBlob, 'test-recording.webm');
      formData.append('language', selectedLanguage);
      formData.append('useKokoro', 'true');
      
      // Send to backend for transcription
      const response = await api.post('/voice/transcribe', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('Transcription response:', response.data); // Debug log
      
      if (response.data.success) {
        // The backend returns transcription directly at the root level
        const transcriptionText = response.data.transcription || 
                                response.data.text || 
                                response.data.result?.transcription ||
                                response.data.result?.text ||
                                '';
        
        if (transcriptionText) {
          setTestTranscription(transcriptionText);
          setShowTestResult(true);
          
          // Update stats
          const wordCount = transcriptionText.split(/\s+/).filter((w: string) => w).length;
          const wpm = Math.round((wordCount / testRecordingTime) * 60);
          
          toast.success(`Transcribed! ${wordCount} words at ${wpm} WPM`, {
            duration: 3000,
            icon: '✨'
          });
        } else {
          console.error('No transcription text found in response:', response.data);
          toast.error('No transcription text received');
        }
      } else {
        console.error('Transcription failed:', response.data);
        toast.error(response.data.message || 'Failed to transcribe audio');
      }
    } catch (error: any) {
      console.error('Transcription error:', error);
      
      // Check for specific error types
      if (error.response?.status === 401) {
        toast.error('Please log in to use voice transcription');
      } else if (error.response?.status === 500) {
        toast.error('Transcription service unavailable. Please ensure Whisper is installed.');
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('Failed to process recording');
      }
    }
  };

  const copyTestTranscription = () => {
    if (testTranscription) {
      navigator.clipboard.writeText(testTranscription);
      toast.success('Copied to clipboard!');
    }
  };

  const clearTestTranscription = () => {
    setTestTranscription('');
    setShowTestResult(false);
    setTestRecordingTime(0);
  };

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Spanish', flag: '🇪🇸' },
    { code: 'fr', name: 'French', flag: '🇫🇷' },
    { code: 'de', name: 'German', flag: '🇩🇪' },
    { code: 'it', name: 'Italian', flag: '🇮🇹' },
    { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
    { code: 'ru', name: 'Russian', flag: '🇷🇺' },
    { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
    { code: 'ko', name: 'Korean', flag: '🇰🇷' },
    { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
    { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
    { code: 'hi', name: 'Hindi', flag: '🇮🇳' }
  ];

  const voice = useVoiceStream(localStorage.getItem('jwt') || '');

  // Sync voice.recording with UI state variable
  useEffect(() => {
    setIsTestRecording(voice.recording);
  }, [voice.recording]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      {/* Overlay */}
      {overlayEnabled && <VocalsPerMinuteOverlay visible={overlayEnabled} vpm={lastVpm} textPreview={lastText} position={bubblePosition} />}

      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
            <FiMic className="text-blue-400" />
            Vocals Per Minute
          </h1>
          <p className="text-gray-400">Transform your voice into text instantly - anywhere on your PC</p>
        </div>

        {/* Test Recording Card */}
        <div className="bg-gray-800 rounded-xl p-6 mb-6 border border-gray-700 shadow-xl">
          <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
            <FiActivity className="text-green-400" />
            Try It Now (Browser Mode)
          </h2>

          <p className="text-gray-300 mb-6">
            Hold the button below to record your voice. Release to transcribe.
          </p>

          {/* Recording Button */}
          <div className="flex flex-col items-center gap-6">
            <button
              onMouseDown={() => {
                setIsTestRecording(true);
                startTestRecording();
              }}
              onMouseUp={() => {
                setIsTestRecording(false);
                stopTestRecording();
              }}
              onTouchStart={() => {
                setIsTestRecording(true);
                startTestRecording();
              }}
              onTouchEnd={() => {
                setIsTestRecording(false);
                stopTestRecording();
              }}
              className={`
                relative w-32 h-32 rounded-full flex items-center justify-center
                transition-all duration-200 transform
                ${isTestRecording
                  ? 'bg-red-500 scale-110 shadow-2xl shadow-red-500/50'
                  : 'bg-blue-500 hover:bg-blue-600 hover:scale-105 shadow-lg'
                }
              `}
            >
              {isTestRecording ? (
                <FiMicOff className="w-12 h-12 text-white animate-pulse" />
              ) : (
                <FiMic className="w-12 h-12 text-white" />
              )}

              {isTestRecording && (
                <div className="absolute -bottom-12 text-sm text-gray-300 whitespace-nowrap">
                  Recording... {testRecordingTime}s
                </div>
              )}
            </button>

            <div className="text-center">
              <p className="text-gray-400 text-sm">
                {isTestRecording ? 'Recording... Release to stop' : 'Hold to record'}
              </p>
            </div>
          </div>

          {/* Transcription Result */}
          {showTestResult && testTranscription && (
            <div className="mt-6 bg-gray-900 rounded-lg p-4 border border-gray-600">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <FiCheck className="text-green-400" />
                  Transcription
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={copyTestTranscription}
                    className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                    title="Copy to clipboard"
                  >
                    <FiCopy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={clearTestTranscription}
                    className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                    title="Clear"
                  >
                    <FiX className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-gray-200 whitespace-pre-wrap">{testTranscription}</p>
              <div className="mt-2 text-sm text-gray-500">
                {testTranscription.split(/\s+/).filter(w => w).length} words • {testRecordingTime}s recording
              </div>
            </div>
          )}
        </div>

        {/* Statistics Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center gap-3 mb-2">
              <FiBarChart className="text-blue-400 w-6 h-6" />
              <h3 className="text-lg font-semibold">Total Words</h3>
            </div>
            <p className="text-3xl font-bold text-blue-400">{stats.totalWords.toLocaleString()}</p>
          </div>

          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center gap-3 mb-2">
              <FiClock className="text-green-400 w-6 h-6" />
              <h3 className="text-lg font-semibold">Time Saved</h3>
            </div>
            <p className="text-3xl font-bold text-green-400">{formatTimeSaved(stats.timeSaved)}</p>
          </div>

          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center gap-3 mb-2">
              <FiActivity className="text-purple-400 w-6 h-6" />
              <h3 className="text-lg font-semibold">Average WPM</h3>
            </div>
            <p className="text-3xl font-bold text-purple-400">{Math.round(stats.averageWPM)}</p>
          </div>

          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center gap-3 mb-2">
              <FiZap className="text-yellow-400 w-6 h-6" />
              <h3 className="text-lg font-semibold">Sessions</h3>
            </div>
            <p className="text-3xl font-bold text-yellow-400">{stats.sessionsCount}</p>
          </div>
        </div>

        {/* Desktop App Download */}
        <div className="bg-gradient-to-br from-blue-900/50 to-purple-900/50 rounded-xl p-8 mb-6 border border-blue-700/50">
          <div className="flex items-start gap-6">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center">
                <FiDownload className="w-8 h-8 text-white" />
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-3">Desktop App - Full Power Mode</h2>
              <p className="text-gray-300 mb-4">
                Install the desktop app for system-wide voice input with hotkey support.
                Works in any application - Word, Excel, browser, IDE, and more!
              </p>

              <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-6 text-sm text-gray-300">
                <li className="flex items-center gap-2">
                  <FiCheck className="text-green-400 flex-shrink-0" />
                  <span>System-wide hotkey (Shift to record)</span>
                </li>
                <li className="flex items-center gap-2">
                  <FiCheck className="text-green-400 flex-shrink-0" />
                  <span>Auto-paste to any application</span>
                </li>
                <li className="flex items-center gap-2">
                  <FiCheck className="text-green-400 flex-shrink-0" />
                  <span>WordPad integration</span>
                </li>
                <li className="flex items-center gap-2">
                  <FiCheck className="text-green-400 flex-shrink-0" />
                  <span>Real-time formatting</span>
                </li>
                <li className="flex items-center gap-2">
                  <FiCheck className="text-green-400 flex-shrink-0" />
                  <span>Multi-language support</span>
                </li>
                <li className="flex items-center gap-2">
                  <FiCheck className="text-green-400 flex-shrink-0" />
                  <span>Productivity statistics</span>
                </li>
              </ul>

              <button
                onClick={downloadDesktopApp}
                disabled={isDownloading}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold
                         transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiDownload />
                {isDownloading ? 'Downloading...' : 'Download Desktop App'}
              </button>
            </div>
          </div>
        </div>

        {/* Settings */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
            <FiSettings className="text-gray-400" />
            Settings
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Language Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <FiGlobe className="inline mr-2" />
                Transcription Language
              </label>
              <select
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {languages.map(lang => (
                  <option key={lang.code} value={lang.code}>
                    {lang.flag} {lang.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Sound Effects */}
            <div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={soundEnabled}
                  onChange={(e) => setSoundEnabled(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-300 flex items-center gap-2">
                  <FiVolume2 />
                  Enable water drop sounds
                </span>
              </label>
            </div>

            {/* Overlay */}
            <div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={overlayEnabled}
                  onChange={(e) => setOverlayEnabled(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-300 flex items-center gap-2">
                  <FiMonitor />
                  Show on-screen overlay
                </span>
              </label>
            </div>

            {/* Auto-format */}
            <div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hotkeyConfig.autoFormat}
                  onChange={(e) => updateHotkeyConfig({ autoFormat: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-300 flex items-center gap-2">
                  <FiCommand />
                  Auto-format text
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VocalsPerMinute;
